import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { STORAGE_KEYS } from '@/constants';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
	theme: Theme;
	setTheme: (theme: Theme) => void;
}

/**
 * Theme preference. Persisted so it survives reloads. The `.dark` class is
 * applied to <html> by `applyTheme` and by the inline script in index.html
 * (which runs before React mounts to avoid a flash of the wrong theme).
 */
export const useThemeStore = create<ThemeState>()(
	persist(
		(set) => ({
			theme: 'system',
			setTheme: (theme) => set({ theme }),
		}),
		{ name: STORAGE_KEYS.theme },
	),
);

export const darkModeMediaQuery = '(prefers-color-scheme: dark)';

/** Resolve a preference to the concrete theme that should be displayed. */
export function resolveTheme(theme: Theme): 'light' | 'dark' {
	if (theme === 'system') {
		return window.matchMedia(darkModeMediaQuery).matches ? 'dark' : 'light';
	}

	return theme;
}

/** Apply the resolved theme to the document root. */
export function applyTheme(theme: Theme): void {
	const resolved = resolveTheme(theme);
	const root = document.documentElement;

	root.classList.toggle('dark', resolved === 'dark');
	root.style.colorScheme = resolved;
}
